import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { useCourses } from '../context/CourseContext';
import { useTemplates } from '../context/TemplateContext';
import { useInstances } from '../context/InstanceContext';
import { LoadingScreen, ErrorScreen, DeleteConfirmDialog, ConfirmDialog } from '../components/ui';
import ViewLayout2 from '../components/ui/ViewLayout2';
import CourseInfoBar from '../components/courses/CourseInfoBar';
import { Logger } from '../utils/logger';
import { notifyError } from '../services/notificationService';

const log = Logger.create('CourseView');

// Tab Components
import CourseTemplatesTab from '../components/courses/CourseTemplatesTab';
import CourseSessionsTab from '../components/courses/CourseSessionsTab';
import CourseStatisticsTab from '../components/courses/CourseStatisticsTab';
import CourseSettingsTab from '../components/courses/CourseSettingsTab';
import CourseParticipantsTab from '../components/courses/CourseParticipantsTab';
import CourseLeaderboardTab from '../components/courses/CourseLeaderboardTab';

/**
 * CourseView2 - Neue Course View mit Unified Design System
 * 
 * Struktur:
 * - Header mit Zurück-Button und Kurs-Name
 * - Action Bar mit Tabs (Vorlagen, Sessions, Statistiken, Einstellungen)
 * - CourseInfoBar mit Icon, Name, Stats
 * - Tab Content je nach aktivem Tab
 */

const TABS = [
  { id: 'templates', label: 'Vorlagen' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'participants', label: 'Teilnehmer' },
  { id: 'leaderboard', label: 'Rangliste' },
  { id: 'statistics', label: 'Statistiken' },
  { id: 'settings', label: 'Einstellungen' }
];

export default function CourseView2() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { user, loading: authLoading } = useUnifiedAuth();
  const { getCourse, updateCourse, deleteCourse } = useCourses();
  const { getTemplatesByCourse, deleteTemplate, duplicateTemplate } = useTemplates();
  const { getInstancesByCourse, startInstance, endInstance, archiveInstance, deleteInstance } = useInstances();
  
  // Data state
  const [course, setCourse] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // UI state
  const activeTab = searchParams.get('tab') || 'templates';
  const [searchQuery, setSearchQuery] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Session action state for ConfirmDialog
  const [sessionAction, setSessionAction] = useState(null); // { type: 'end' | 'archive' | 'delete-course', sessionId?, courseInfo? }

  // Set active tab
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  // Load data when user and courseId are available
  useEffect(() => {
    if (user && courseId && !authLoading && !dataLoaded) {
      loadData();
    }
  }, [user, courseId, authLoading, dataLoaded]);

  const loadData = async () => {
    if (!user) return;
    
    setDataLoading(true);
    try {
      const courseData = await getCourse(courseId);
      setCourse(courseData);

      const courseTemplates = getTemplatesByCourse(courseId);
      setTemplates(courseTemplates);

      const courseInstances = getInstancesByCourse(courseId);
      setInstances(courseInstances);
      
      setDataLoaded(true);
    } catch (error) {
      log.error('Error loading course data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Computed data
  const activeInstances = useMemo(() => 
    instances.filter(i => i.isActive || i.status === 'active'), 
    [instances]
  );

  const totalQuizzes = instances.length;

  // Update tabs with counts
  const tabsWithCounts = useMemo(() => 
    TABS.map(tab => {
      if (tab.id === 'templates') return { ...tab, count: templates.length };
      if (tab.id === 'sessions') return { ...tab, count: activeInstances.length };
      return tab;
    }), 
    [templates.length, activeInstances.length]
  );

  // Course stats for InfoBar
  const courseStats = [
    { value: templates.length, label: 'Vorlagen' },
    { value: activeInstances.length, label: 'aktive Sessions' },
    { value: totalQuizzes, label: 'Quizze' }
  ];

  // Handlers
  const handleCreateTemplate = () => {
    navigate(`/template/new?courseId=${courseId}`);
  };

  const handleStartQuiz = async (templateId) => {
    try {
      const instance = await startInstance(templateId);
      navigate(`/live/${instance.id}`);
    } catch (error) {
      notifyError('Fehler beim Starten des Quiz: ' + error.message);
    }
  };

  const handleEditTemplate = (templateId) => {
    navigate(`/template/${templateId}`);
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await duplicateTemplate(templateId);
      loadData();
    } catch (error) {
      notifyError('Fehler beim Duplizieren: ' + error.message);
    }
  };

  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await deleteTemplate(templateToDelete.id);
      setTemplateToDelete(null);
      loadData();
    } catch (error) {
      notifyError('Fehler beim Löschen: ' + error.message);
    }
  };

  const handleEndSession = async (sessionId) => {
    setSessionAction({ type: 'end', sessionId });
  };

  const handleArchiveSession = async (sessionId) => {
    setSessionAction({ type: 'archive', sessionId });
  };

  // Execute session action after confirmation
  const executeSessionAction = async () => {
    if (!sessionAction) return;
    
    try {
      if (sessionAction.type === 'end') {
        await endInstance(sessionAction.sessionId);
      } else if (sessionAction.type === 'archive') {
        await archiveInstance(sessionAction.sessionId);
      } else if (sessionAction.type === 'delete-session') {
        await deleteInstance(sessionAction.sessionId);
      } else if (sessionAction.type === 'delete-course') {
        await deleteCourse(courseId);
        setSessionAction(null);
        navigate('/dashboard');
        return;
      }
      loadData();
    } catch (error) {
      notifyError('Fehler: ' + error.message);
    } finally {
      setSessionAction(null);
    }
  };

  const handleUpdateCourse = async (formData) => {
    setIsSubmitting(true);
    try {
      await updateCourse(courseId, formData);
      setCourse({ ...course, ...formData });
    } catch (error) {
      notifyError('Fehler beim Aktualisieren: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCourse = async () => {
    setSessionAction({ type: 'delete-course', courseInfo: course?.name });
  };

  const handleDeleteSession = async (sessionId) => {
    setSessionAction({ type: 'delete-session', sessionId });
  };

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.name?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // Loading state - wait for auth and course data
  if (authLoading) {
    return <LoadingScreen message="Prüfe Anmeldung..." />;
  }

  // Not authenticated - redirect to login
  if (!user) {
    navigate('/');
    return null;
  }

  // Data loading
  if (dataLoading || !dataLoaded) {
    return <LoadingScreen message="Lade Kurs..." />;
  }

  // Not found
  if (!course) {
    return (
      <ErrorScreen 
        type="notFound" 
        message="Kurs nicht gefunden" 
        backTo="/dashboard"
        backLabel="Zum Dashboard"
      />
    );
  }

  return (
    <ViewLayout2
      title={course.name}
      onBack={() => navigate('/dashboard')}
      tabs={tabsWithCounts}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      searchPlaceholder="Vorlagen suchen..."
      onSearch={setSearchQuery}
      primaryAction={{
        label: 'Neue Vorlage',
        onClick: handleCreateTemplate
      }}
      secondaryActions={[
        { label: 'KI Generator', onClick: () => navigate(`/llm-generator?courseId=${courseId}`) }
      ]}
    >
      {/* Course Info Bar */}
      <CourseInfoBar
        icon={course.icon}
        name={course.name}
        description={course.description}
        color={course.color}
        stats={courseStats}
      />
      
      {/* Tab Content */}
      {activeTab === 'templates' && (
        <CourseTemplatesTab 
          templates={filteredTemplates}
          onStartQuiz={handleStartQuiz}
          onEdit={handleEditTemplate}
          onDuplicate={handleDuplicateTemplate}
          onDelete={handleDeleteTemplate}
          onCreate={handleCreateTemplate}
        />
      )}
      
      {activeTab === 'sessions' && (
        <CourseSessionsTab 
          instances={instances}
          activeInstances={activeInstances}
          onEndSession={handleEndSession}
          onArchiveSession={handleArchiveSession}
          onDeleteSession={handleDeleteSession}
        />
      )}
      
      {activeTab === 'statistics' && (
        <CourseStatisticsTab 
          templates={templates}
          instances={instances}
        />
      )}
      
      {activeTab === 'participants' && (
        <CourseParticipantsTab 
          course={course}
          courseId={courseId}
        />
      )}
      
      {activeTab === 'leaderboard' && (
        <CourseLeaderboardTab 
          course={course}
          courseId={courseId}
        />
      )}
      
      {activeTab === 'settings' && (
        <CourseSettingsTab 
          course={course}
          onUpdate={handleUpdateCourse}
          onDelete={handleDeleteCourse}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Template Confirmation */}
      <DeleteConfirmDialog
        isOpen={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={confirmDeleteTemplate}
        itemName={templateToDelete?.name || 'Vorlage'}
      />
      
      {/* Session Action Confirmation */}
      <ConfirmDialog
        isOpen={!!sessionAction}
        onClose={() => setSessionAction(null)}
        onConfirm={executeSessionAction}
        title={
          sessionAction?.type === 'end' ? 'Session beenden' :
          sessionAction?.type === 'archive' ? 'Session archivieren' :
          sessionAction?.type === 'delete-session' ? 'Session löschen' :
          sessionAction?.type === 'delete-course' ? 'Kurs löschen' : ''
        }
        message={
          sessionAction?.type === 'end' ? 'Möchtest du diese Session wirklich beenden? Alle Teilnehmer werden von der Session getrennt.' :
          sessionAction?.type === 'archive' ? 'Möchtest du diese Session wirklich archivieren? Die Session kann danach nicht mehr gestartet werden.' :
          sessionAction?.type === 'delete-session' ? 'Möchtest du diese Session wirklich löschen? Alle Daten und Statistiken werden unwiderruflich gelöscht!' :
          sessionAction?.type === 'delete-course' ? `Möchtest du den Kurs "${sessionAction?.courseInfo}" wirklich löschen? Alle Vorlagen und Sessions werden unwiderruflich gelöscht!` : ''
        }
        confirmLabel={
          sessionAction?.type === 'end' ? 'Session beenden' :
          sessionAction?.type === 'archive' ? 'Archivieren' :
          sessionAction?.type === 'delete-session' ? 'Session löschen' :
          sessionAction?.type === 'delete-course' ? 'Kurs löschen' : 'Bestätigen'
        }
        variant={(sessionAction?.type === 'delete-course' || sessionAction?.type === 'delete-session') ? 'danger' : 'warning'}
      />
    </ViewLayout2>
  );
}
